import { Component, Inject, ViewChild } from '@angular/core';
import { MAT_DIALOG_DATA } from '@angular/material/dialog';
import { combineAll, DcBaseComponent } from '@datachain/ui-sdk/common';
import {
	AppModalService,
	ModalData,
} from '@datachain/ui-sdk/components/app-modal';
import { BehaviorSubject, firstValueFrom, map, Observable } from 'rxjs';

import { DcIcons } from '../../../../ui/app-dc.icons';
import { SubscriptionDiagnosticError } from '../../../domain/diagnostic-errors.entity';
import { PublicMetadataEntity } from '../../../domain/public-metadata.entity';
import { SubscriptionProvider } from '../../../providers/subscription.provider';
import { SubscriptionSelector } from '../../../store';
import { PublicMetadataConfigComponent } from '../../public-metadata-config/public-metadata-config.component';

@Component({
	selector: 'app-public-metadata-popup',
	templateUrl: './public-metadata-popup.component.html',
	styleUrls: ['./public-metadata-popup.component.scss'],
	providers: [SubscriptionProvider],
})
export class PublicMetadataPopupComponent<
	T extends { metadata: PublicMetadataEntity }
> extends DcBaseComponent {
	protected readonly DcIcons = DcIcons;

	@ViewChild(PublicMetadataConfigComponent)
	public subscriptionPublicMetadataCmp: PublicMetadataConfigComponent | null = null;

	public vo$: Observable<{
		publicMetadata: PublicMetadataEntity;
		error: SubscriptionDiagnosticError | undefined;
		isValid: boolean;
	}>;

	private readonly isValid$ = new BehaviorSubject<boolean>(true);

	public constructor(
		private readonly subscriptionProvider: SubscriptionProvider,
		private readonly modalService: AppModalService,
		private readonly selector: SubscriptionSelector,
		@Inject(MAT_DIALOG_DATA) public readonly modalData: ModalData & T
	) {
		super();
		this.cmpId = 'public-metadata-popup';

		this.vo$ = combineAll({
			publicMetadata: this.modalData.metadata,
			isValid: this.isValid$,
			error: this.subscriptionProvider.errors$.pipe(
				map((errors) => errors.valueSeq().first())
			),
		});
	}

	public async publicMetadataChanged(): Promise<void> {
		if (!this.subscriptionPublicMetadataCmp) {
			return;
		}

		const initialMetadata = await firstValueFrom(
			this.selector.getPublicMetadata$()
		);

		const currentMetadata =
			await this.subscriptionPublicMetadataCmp.extractFormData();

		const hasChanged = !initialMetadata.isEqualTo(currentMetadata);

		this.isValid$.next(
			hasChanged
				? (await firstValueFrom(this.subscriptionProvider.errors$)).size === 0
				: false
		);
	}

	public async updateAction(): Promise<void> {
		if (!this.subscriptionPublicMetadataCmp) {
			return;
		}

		const metadataConfig =
			await this.subscriptionPublicMetadataCmp.extractFormData();

		this.modalService.closeActiveModal({
			metadataConfig,
		});
	}
}
